import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, Quote, PenLine, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import GoogleReviews from "@/components/GoogleReviews";
import { toast } from "sonner";

export default function Testimonials() {
  const { user } = useAuth();
  const [writing, setWriting] = useState(false);
  const { data: items = [], isLoading } = useQuery({ queryKey: ["testimonials"], queryFn: () => api.get("/testimonials").then((r) => r.data) });
  const featured = items.find((t) => t.featured);
  const rest = items.filter((t) => !t.featured);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="testimonials-page">
      <Seo
        title="Reviews & Testimonials"
        description="Hear from community members and guests about their experiences at Enhancing Your World wellness centers in Newport and Verona, KY."
        path="/testimonials"
      />
      <div className="flex justify-between items-end flex-wrap gap-4 mb-12">
        <div>
          <p className="eyw-label mb-3">Voices from the Ecosystem</p>
          <h1 className="font-serif text-4xl lg:text-6xl text-[#1A242B]">Testimonials</h1>
        </div>
        <button onClick={() => user ? setWriting(true) : toast.error("Please sign in to share your experience.")}
          data-testid="write-testimonial-btn" className="eyw-btn !py-2.5 text-sm"><PenLine size={16} /> Share your experience</button>
      </div>

      {isLoading && <p className="text-[#4A5D6B]">Loading…</p>}

      {featured && (
        <div data-testid="featured-testimonial" className="rounded-3xl bg-[#144F75] text-white p-10 lg:p-16 mb-10 relative overflow-hidden">
          <Quote className="absolute -top-4 -left-2 text-white/10" size={140} />
          <div className="relative flex flex-col lg:flex-row gap-10 items-center">
            {featured.avatar && <img src={featured.avatar} alt={featured.name} className="h-28 w-28 rounded-full object-cover ring-4 ring-white/20" />}
            <div>
              <div className="flex gap-1 mb-4">{Array.from({ length: featured.rating }).map((_, i) => <Star key={i} size={17} className="fill-[#D98E73] text-[#D98E73]" />)}</div>
              <p className="font-serif text-2xl lg:text-4xl italic leading-snug">"{featured.quote}"</p>
              <p className="mt-6 text-white/80"><span className="font-semibold">{featured.name}</span> · {featured.role}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rest.map((t) => (
          <div key={t.id} data-testid={`testimonial-card-${t.id}`} className="eyw-card p-8">
            <div className="flex gap-1 mb-4">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={15} className="fill-[#D98E73] text-[#D98E73]" />)}</div>
            <p className="font-serif text-xl text-[#144F75] italic leading-relaxed">"{t.quote}"</p>
            <div className="flex items-center gap-3 mt-6">
              {t.avatar && <img src={t.avatar} alt={t.name} className="h-11 w-11 rounded-full object-cover" />}
              <div>
                <div className="font-semibold text-sm text-[#1A242B]">{t.name}</div>
                <div className="text-xs text-[#4A5D6B]">{t.role}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-16 pt-10 border-t border-[#E5E0D8]">
        <GoogleReviews title="What people say on Google" />
      </div>

      {writing && <WriteModal onClose={() => setWriting(false)} />}
    </div>
  );
}

function WriteModal({ onClose }) {
  const qc = useQueryClient();
  const [rating, setRating] = useState(5);
  const [subjectType, setSubjectType] = useState("ecosystem");
  const [subjectName, setSubjectName] = useState("");
  const [quote, setQuote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!quote.trim()) return toast.error("Please write a short testimonial.");
    setSaving(true);
    try {
      await api.post("/testimonials/submit", { quote, rating, subject_type: subjectType, subject_name: subjectName });
      toast.success("Thank you! Your testimonial is pending review.");
      qc.invalidateQueries({ queryKey: ["testimonials"] });
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="write-testimonial-modal">
        <h3 className="font-serif text-xl text-[#1A242B] mb-4">Share your experience</h3>
        <div className="flex items-center gap-3 mb-4">
          <span className="text-sm font-medium">Your rating</span>
          <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" onClick={() => setRating(n)} data-testid={`testimonial-star-${n}`}>
              <Star size={22} className={n <= rating ? "fill-[#D98E73] text-[#D98E73]" : "text-[#D3Cabb]"} />
            </button>))}
          </div>
        </div>
        <div className="mb-3"><label className="text-sm font-medium mb-1 block">About</label>
          <select value={subjectType} onChange={(e) => setSubjectType(e.target.value)} data-testid="testimonial-subject-type" className="eyw-input w-full">
            <option value="ecosystem">The ecosystem overall</option>
            <option value="center">A center</option>
            <option value="service">A service</option>
            <option value="enhancer">An enhancer</option>
          </select></div>
        {subjectType !== "ecosystem" && (
          <div className="mb-3"><label className="text-sm font-medium mb-1 block">Name (optional)</label>
            <input value={subjectName} onChange={(e) => setSubjectName(e.target.value)} data-testid="testimonial-subject-name" className="eyw-input w-full" placeholder="e.g. Newport center" /></div>
        )}
        <div className="mb-3"><label className="text-sm font-medium mb-1 block">Your experience</label>
          <textarea rows={4} value={quote} onChange={(e) => setQuote(e.target.value)} data-testid="testimonial-body" className="eyw-input w-full resize-none" /></div>
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="testimonial-submit" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} Submit</button>
        </div>
      </div>
    </div>
  );
}

